import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '../platform/firebase/clients';
import {
  TaskTypeInstructions,
  TaskTypeSettings,
} from '../types';
import { isGoogleDocUrl } from '../taskTypes/instructions';
import { wrapSubscriptionError } from './subscriptionError';

const TASK_TYPE_SETTINGS = 'taskTypeSettings';

function toDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function mapInstructions(raw: unknown): TaskTypeInstructions | null {
  if (!raw || typeof raw !== 'object') return null;
  const kind = (raw as { kind?: unknown }).kind;
  const value = (raw as { value?: unknown }).value;
  if ((kind !== 'google-doc-url' && kind !== 'markdown') || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (kind === 'google-doc-url' && !isGoogleDocUrl(trimmed)) return null;
  return { kind, value: trimmed };
}

function mapTaskTypeSettings(id: string, data: Record<string, unknown>): TaskTypeSettings {
  return {
    taskTypeId: typeof data.taskTypeId === 'string' && data.taskTypeId.trim() ? data.taskTypeId : id,
    instructions: mapInstructions(data.instructions),
    updatedAt: toDateOrNull(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : null,
    updatedByName: typeof data.updatedByName === 'string' ? data.updatedByName : null,
  };
}

/**
 * One-shot read of the admin-managed task type settings collection. The Task
 * Types admin page doesn't need a live collection subscription; it loads once
 * and then owns optimistic local edits.
 */
export async function listTaskTypeSettings(): Promise<TaskTypeSettings[]> {
  const snap = await getDocs(collection(db, TASK_TYPE_SETTINGS));
  return snap.docs.map((d) => mapTaskTypeSettings(d.id, d.data()));
}

/**
 * Live subscription to one task type's instructions. Missing doc resolves to
 * `null` so the shell can simply hide the link.
 */
export function subscribeToTaskTypeInstructions(
  taskTypeId: string,
  onChange: (instructions: TaskTypeInstructions | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, TASK_TYPE_SETTINGS, taskTypeId),
    (snap) => onChange(snap.exists() ? mapTaskTypeSettings(snap.id, snap.data()).instructions : null),
    wrapSubscriptionError('taskTypeSettings.subscribeToTaskTypeInstructions', onError),
  );
}

/**
 * Admin-only upsert of one task type's instructions. Passing `null` clears
 * the instructions by deleting the settings doc entirely.
 */
export async function saveTaskTypeInstructions(
  taskTypeId: string,
  actor: FirebaseUser,
  instructions: TaskTypeInstructions | null,
): Promise<void> {
  const ref = doc(db, TASK_TYPE_SETTINGS, taskTypeId);
  if (!instructions) {
    await deleteDoc(ref);
    return;
  }
  await setDoc(ref, {
    taskTypeId,
    instructions,
    updatedAt: serverTimestamp(),
    updatedBy: actor.uid,
    updatedByName: actor.displayName ?? null,
  });
}
